// Public marketing page at "/" (Track C).
//
// Structure decision, per the sprint's "pick whichever is less
// disruptive": the app did NOT move to /app. This page renders only
// for signed-out visitors (SignedOutRoutes); a signed-in coordinator
// at "/" still lands on their dashboard, volunteers still bounce to
// /:parish/dashboard, and every volunteer/public URL is untouched.
// Zero existing links change.
//
// Copy rules (C2): no em dashes in rendered copy, sentence case
// headings, product screenshots only (real captures from qa/shots,
// copied to /marketing/). Hero copy is Michael's v1 draft and
// expected to be edited for voice.
//
// Motion (borrowed from the OfferVane marketing site's playbook):
// scroll-reveal via IntersectionObserver (.reveal + .is-in, staggered
// by a --d custom property), a sticky header that grows a hairline +
// blur once scrolled, and a gentle rAF-throttled parallax on the hero
// screenshot. All of it collapses to static under
// prefers-reduced-motion, and the reveal styles are scoped inside
// .landing so no other surface inherits them.

import { useEffect } from "react";
import { Link } from "react-router-dom";
import SacristyLogo from "../components/SacristyLogo";

interface Pillar {
  title: string;
  body: string;
  image: string;
  alt: string;
  // Rendered inside a phone frame (volunteer-facing surfaces are
  // phone-first; showing them at phone size answers "what do MY
  // volunteers see?").
  phone?: boolean;
}

const PILLARS: Pillar[] = [
  {
    title: "Onboard",
    body:
      "A parish-branded help center and a volunteer portal where every volunteer sees their own schedule, their own Masses, and exactly what to do next. New volunteers stop feeling lost in week one.",
    image: "/marketing/volunteer-phone.png",
    alt: "The volunteer dashboard on a phone: my Masses, my ministry for each, and who is serving with me",
    phone: true,
  },
  {
    title: "Fill",
    body:
      "The dashboard reads your schedule straight from MSP: every Mass this week, every open position, every sub request, and who is already serving.",
    image: "/marketing/coverage.png",
    alt: "A Mass detail view showing a position that needs coverage and the full serving roster",
  },
  {
    title: "Ask Sacristy",
    body:
      "Ask who is serving this weekend, who could cover an open spot, or who might need a kind check-in. It answers from your live schedule and drafts the emails and texts for you to send. Nothing happens without you.",
    image: "/marketing/ask.png",
    alt: "Ask Sacristy answering a coordinator's question from the live schedule",
  },
];

const PAINS = [
  {
    title: "The same questions, forever",
    body:
      "When am I serving? How do I request a sub? Fair questions, every one. And every one lands on one coordinator's phone, every single week.",
  },
  {
    title: "Finding your one Mass",
    body:
      "The full schedule holds every Mass and every ministry, which is a lot of rows. A volunteer just wants their own Mass, so they call the coordinator to be sure.",
  },
  {
    title: "When life gets busy",
    body:
      "A volunteer's season changes, and nobody notices until a Mass is short-handed. Not for lack of caring, for lack of seeing. A kind check-in weeks earlier changes that story.",
  },
];

// OfferVane's scroll interactions, translated to a React effect.
// Everything is queried inside the effect (post-render) and cleaned
// up on unmount; reduced-motion short-circuits to static.
function useLandingMotion() {
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const cleanups: (() => void)[] = [];

    // Scroll reveal with stagger.
    const reveals = document.querySelectorAll<HTMLElement>(".landing .reveal");
    reveals.forEach((el) => {
      const d = el.getAttribute("data-delay");
      if (d) el.style.setProperty("--d", d);
    });
    if (prefersReduced || !("IntersectionObserver" in window)) {
      reveals.forEach((el) => el.classList.add("is-in"));
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add("is-in");
              io.unobserve(e.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
      );
      reveals.forEach((el) => io.observe(el));
      cleanups.push(() => io.disconnect());
    }

    // Sticky header hairline.
    const header = document.querySelector<HTMLElement>(".landing__header");
    if (header) {
      const onScroll = () => header.classList.toggle("is-stuck", window.scrollY > 8);
      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });
      cleanups.push(() => window.removeEventListener("scroll", onScroll));
    }

    // Hero screenshot parallax: drifts up a touch as you scroll past.
    const shot = document.querySelector<HTMLElement>(".landing__shot--hero");
    if (shot && !prefersReduced) {
      let ticking = false;
      const update = () => {
        ticking = false;
        const r = shot.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const total = vh + r.height;
        const p = total > 0 ? Math.max(0, Math.min(1, (vh - r.top) / total)) : 0;
        shot.style.setProperty("--parallax", `${(-18 * p).toFixed(1)}px`);
      };
      const queue = () => {
        if (!ticking) {
          requestAnimationFrame(update);
          ticking = true;
        }
      };
      window.addEventListener("scroll", queue, { passive: true });
      window.addEventListener("resize", queue, { passive: true });
      update();
      cleanups.push(() => {
        window.removeEventListener("scroll", queue);
        window.removeEventListener("resize", queue);
      });
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);
}

export default function Landing() {
  useLandingMotion();

  return (
    <main className="landing">
      <header className="landing__header">
        <SacristyLogo />
        <nav className="landing__nav">
          <a className="landing__nav-link" href="#early-access">
            Early access
          </a>
          <Link className="landing__nav-link" to="/sign-in">
            Sign in
          </Link>
          <Link className="btn landing__nav-cta" to="/signup">
            Join the list
          </Link>
        </nav>
      </header>

      <section className="landing__hero">
        <h1 className="landing__h1 reveal">
          Your volunteers, confident.
          <br />
          Your Masses, covered.
        </h1>
        <p className="landing__subhead reveal" data-delay="1">
          Sacristy AI sits on top of Ministry Scheduler Pro and gives your volunteers
          instant answers to the questions they keep asking, so your coordinator stops
          being the help desk.
        </p>
        <div className="landing__cta-row reveal" data-delay="2">
          <Link className="btn landing__cta" to="/signup">
            Join the early access list
          </Link>
          {/* Says exactly what the anchor delivers (screenshots).
              "See it in action" returns when a real demo exists. */}
          <a className="landing__see" href="#product">
            See what it looks like
          </a>
        </div>
        <figure className="landing__shot landing__shot--hero reveal" data-delay="3">
          <img
            src="/marketing/dashboard.png"
            alt="The Sacristy AI dashboard: this week's Masses, positions needing attention, and the Ask Sacristy panel"
            loading="eager"
          />
        </figure>
      </section>

      <section className="landing__section">
        <p className="landing__kicker reveal">The problem</p>
        <h2 className="landing__h2 reveal" data-delay="1">
          Every parish coordinator answers the same questions, week after week. It
          doesn't have to be that way.
        </h2>
        <div className="landing__pains">
          {PAINS.map((p, i) => (
            <div key={p.title} className="landing__pain reveal" data-delay={String(i + 1)}>
              <h3 className="landing__h3">{p.title}</h3>
              <p>{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="landing__section" id="product">
        <p className="landing__kicker reveal">The product</p>
        <h2 className="landing__h2 reveal" data-delay="1">
          Built on top of Ministry Scheduler Pro, the scheduler you already use
        </h2>
        {PILLARS.map((pillar, i) => (
          <div
            key={pillar.title}
            className={`landing__pillar${i % 2 === 1 ? " landing__pillar--flip" : ""}`}
          >
            <div className="landing__pillar-text reveal">
              <h3 className="landing__h3">{pillar.title}</h3>
              <p>{pillar.body}</p>
            </div>
            <figure
              className={`landing__shot${pillar.phone ? " landing__shot--phone" : ""} reveal`}
              data-delay="1"
            >
              <img src={pillar.image} alt={pillar.alt} loading="lazy" />
            </figure>
          </div>
        ))}
      </section>

      <section className="landing__section">
        <p className="landing__kicker reveal">How it works</p>
        <h2 className="landing__h2 reveal" data-delay="1">
          Live before next Sunday
        </h2>
        <ol className="landing__steps">
          <li className="landing__step reveal" data-delay="1">
            <h3 className="landing__h3">1. Connect your MSP account</h3>
            <p>
              Paste your MSP API token; read-only, about 2 minutes. Sacristy AI never changes
              your MSP data. Requires MSP Plus Edition.
            </p>
          </li>
          <li className="landing__step reveal" data-delay="2">
            <h3 className="landing__h3">2. Invite your volunteers</h3>
            <p>
              Send one email from a template we draft for you. Volunteers get their own
              portal and a help center in your parish's name.
            </p>
          </li>
          <li className="landing__step reveal" data-delay="3">
            <h3 className="landing__h3">3. Watch your phone go quiet</h3>
            <p>
              Volunteers find their own answers. You see an open spot weeks early, while
              there is still time to fill it gently, and Ask Sacristy handles the messy
              weeks.
            </p>
          </li>
        </ol>
      </section>

      <section className="landing__section landing__section--center" id="early-access">
        <p className="landing__kicker reveal">Early access</p>
        <h2 className="landing__h2 reveal" data-delay="1">
          We're onboarding parishes a few at a time
        </h2>
        <div className="landing__access-card reveal" data-delay="2">
          <p className="landing__access-lead">
            Add your parish to the list and we'll bring you on with the next onboarding
            group, so every parish that starts gets a real setup.
          </p>
          <ul className="landing__access-list">
            <li>No card to start</li>
            <li>Unlimited volunteers and ministries</li>
            <li>Help center, volunteer portal, dashboard, and Ask Sacristy</li>
            <li>Your data stays in MSP, and you can leave whenever you like</li>
          </ul>
          <Link className="btn landing__cta" to="/signup">
            Join the early access list
          </Link>
        </div>
      </section>

      <section className="landing__section landing__trust">
        <p className="landing__kicker reveal">Why we built it</p>
        <h2 className="landing__h2 reveal" data-delay="1">
          Made for a real parish first
        </h2>
        <p className="landing__trust-body reveal" data-delay="2">
          Sacristy AI was built by a Catholic UX designer for his own parish, where it runs
          every week. It reads your schedule and never writes to it: your data stays in
          Ministry Scheduler Pro, and your coordinator stays in charge of every decision.
          All of it exists for one reason: so every Mass has the volunteers it needs, and
          everyone who says yes feels confident serving.
        </p>
        {/* Testimonial slot — intentionally empty until Colleen's
            quote lands. Do not fill with placeholder prose. */}
      </section>

      <footer className="landing__footer">
        <p className="landing__mission">
          Built to help parishes staff every Mass they're called to celebrate.
        </p>
        <nav className="landing__footer-links">
          <a className="landing__nav-link" href="/help/connect-msp">
            Connecting to MSP
          </a>
          <a className="landing__nav-link" href="mailto:hello@sacristyai.com">
            Contact
          </a>
          <Link className="landing__nav-link" to="/sign-in">
            Parish staff sign in
          </Link>
        </nav>
      </footer>
    </main>
  );
}
